import React from 'react';
import { Helmet } from 'react-helmet-async';

const LAST_UPDATED = 'July 16, 2026';
const CONTACT_EMAIL = 'info@docxsai.com';
const SUPPORT_EMAIL = 'support@docxsai.com';

const Section = ({ title, children }) => (
  <div>
    <h2 className="text-2xl font-bold mb-3 text-[var(--color-text)]">{title}</h2>
    <div className="text-[var(--color-text-secondary)] text-base sm:text-lg space-y-3 text-justify">{children}</div>
  </div>
);

const TermsPage = () => (
  <div className="flex-1 flex flex-col public-page-glow relative overflow-hidden">
    <Helmet>
      <title>Terms of Service – DocxsAi</title>
      <meta
        name="description"
        content="DocxsAi terms of use covering subscriptions, AI document services, acceptable use, disclaimers, payments, and limitation of liability."
      />
      <link rel="canonical" href="https://docxsai.com/terms" />
    </Helmet>

    <div className="absolute top-10 left-4 w-32 h-32 sm:top-20 sm:left-20 sm:w-80 sm:h-80 bg-gradient-to-br from-[var(--color-gradient-from)]/10 to-[var(--color-gradient-to)]/10 rounded-full blur-3xl pointer-events-none" />
    <div className="absolute top-20 right-4 w-40 h-40 sm:top-40 sm:right-20 sm:w-96 sm:h-96 bg-gradient-to-br from-[var(--color-gradient-to)]/10 to-[var(--color-gradient-from)]/10 rounded-full blur-3xl pointer-events-none" />
    <div className="absolute bottom-16 left-6 w-24 h-24 sm:bottom-32 sm:left-32 sm:w-64 sm:h-64 bg-gradient-to-br from-[var(--color-gradient-from)]/10 to-[var(--color-gradient-to)]/10 rounded-full blur-3xl pointer-events-none" />
    <div className="absolute bottom-10 right-6 w-28 h-28 sm:bottom-20 sm:right-40 sm:w-72 sm:h-72 bg-gradient-to-br from-[var(--color-gradient-to)]/10 to-[var(--color-gradient-from)]/10 rounded-full blur-3xl pointer-events-none" />

    <main className="relative z-10 flex-1 px-4 sm:px-6 pt-28 sm:pt-32 pb-10 sm:pb-12">
      <div className="max-w-6xl w-full mx-auto animate-slideUp">
        <h1 className="text-2xl sm:text-5xl font-bold mb-4 text-[var(--color-text)]">
          <span>Terms</span>{' '}
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-[var(--color-gradient-from)] to-[var(--color-gradient-to)]">&amp; Conditions</span>
        </h1>

        <p className="mb-6 text-[var(--color-text-muted)] text-sm sm:text-base">
          Last updated: {LAST_UPDATED}
        </p>

        <p className="mb-10 text-[var(--color-text-secondary)] text-base sm:text-lg text-justify">
          These Terms of Service (&quot;Terms&quot;) govern your access to and use of DocxsAi&apos;s website, applications,
          and services (the &quot;Service&quot;). DocxsAi is a product of TabsolTech, a CAC-registered company that owns
          and operates the Service. The DocxsAi name and brand are trademarked by TabsolTech. By creating an account,
          subscribing, uploading documents, or otherwise using DocxsAi, you agree to these Terms and our Privacy Policy.
        </p>

        <div className="text-left space-y-10">
          <Section title="1. Eligibility and Account Responsibilities">
            <ul className="list-disc ml-6 space-y-2">
              <li>You must be at least 18 years old to use the Service.</li>
              <li>You must provide accurate registration information and keep your account credentials secure.</li>
              <li>You are responsible for all activity that occurs under your account.</li>
              <li>You must notify us promptly of any unauthorized access or suspected breach.</li>
            </ul>
          </Section>

          <Section title="2. Description of the Service">
            <p>DocxsAi provides AI-assisted tools for working with documents, including but not limited to:</p>
            <ul className="list-disc ml-6 space-y-2">
              <li>Analysis, revamp, and generation of contracts, CVs/resumes, and statements of purpose.</li>
              <li>Subscription-based monthly document allowances and related platform features.</li>
              <li>Hiring shortlist comparison of multiple CVs against a job description, with ranked reports.</li>
              <li>Job board listings, alerts, and SOP opportunity information where available.</li>
            </ul>
            <p>
              Features may change, be added, limited, or removed as we improve the platform. We do not guarantee
              uninterrupted availability of any specific feature.
            </p>
          </Section>

          <Section title="3. Important Disclaimer — Not Professional Advice">
            <p>
              <strong className="text-[var(--color-text)]">DocxsAi does not provide legal, immigration, financial, HR, or other professional advice.</strong>
            </p>
            <p>
              Outputs from the Service are generated automatically and are provided for informational and
              productivity purposes only. AI-generated analysis, suggestions, rankings, and rewritten content may
              be incomplete, inaccurate, outdated, or unsuitable for your specific situation.
            </p>
            <p>
              You are solely responsible for reviewing, validating, and deciding how to use any output before
              signing contracts, submitting applications, making hiring decisions, or taking any other action.
              You should consult qualified professionals where appropriate.
            </p>
          </Section>

          <Section title="4. Your Content and License to Us">
            <ul className="list-disc ml-6 space-y-2">
              <li>You retain ownership of documents and content you upload, subject to third-party rights in that content.</li>
              <li>You represent that you have the right to upload and process the content you submit.</li>
              <li>
                You grant DocxsAi a limited license to host, store, process, analyze, transform, display, and
                transmit your content solely to operate, secure, and improve the Service.
              </li>
              <li>
                You are responsible for maintaining backups of important originals. We are not a document
                archive or records-management service.
              </li>
            </ul>
          </Section>

          <Section title="5. Acceptable Use">
            <p>You agree not to:</p>
            <ul className="list-disc ml-6 space-y-2">
              <li>Use the Service for unlawful, fraudulent, abusive, or harmful purposes.</li>
              <li>Upload malware, unlawful content, or material you do not have permission to process.</li>
              <li>Attempt to reverse engineer, scrape, overload, or disrupt the Service or its security controls.</li>
              <li>Misrepresent AI-generated output as guaranteed fact, official legal advice, or human professional review.</li>
              <li>Use the Service in a way that infringes intellectual property, privacy, or other rights of others.</li>
              <li>Share account access or resell the Service without our written permission.</li>
            </ul>
            <p>
              We may suspend or terminate access if we reasonably believe you have violated these Terms or pose a
              risk to the platform or other users.
            </p>
          </Section>

          <Section title="6. Subscriptions, Usage Limits, and Payments">
            <ul className="list-disc ml-6 space-y-2">
              <li>Paid plans, document allowances, and feature access are described on our pricing pages and in your account.</li>
              <li>Payments are processed by TabsolTech through our payment provider (such as Flutterwave) for DocxsAi purchases. By subscribing, you authorize applicable charges for your selected plan or purchase. Your bank or card statement may show TabsolTech as the merchant.</li>
              <li>Monthly and yearly billing terms, allowances, and plan limits apply as shown at checkout and in your account dashboard.</li>
              <li>Hiring shortlist credits and other one-time purchases are separate from standard subscription document quotas unless expressly stated otherwise.</li>
              <li>Failed payments, chargebacks, or expired subscriptions may result in reduced access until payment is resolved.</li>
              <li>Except where required by law, fees are non-refundable once a billing period or purchased credit pack has been delivered or activated.</li>
            </ul>
            <p>
              Pricing and plan features may change for future billing periods. We will provide notice where required
              before changes affect your subscription.
            </p>
          </Section>

          <Section title="7. Hiring Shortlist">
            <p>
              Hiring shortlist features allow batch comparison of candidate CVs against a job description. Reports
              are generated automatically and may be emailed to your account email address.
            </p>
            <p>
              You are responsible for ensuring lawful collection and use of candidate information, including
              compliance with employment, privacy, and anti-discrimination laws. DocxsAi rankings and commentary are
              decision-support tools only and must not be treated as sole hiring determinations.
            </p>
          </Section>

          <Section title="8. Third-Party Content and Links">
            <p>
              The Service may include job listings, opportunity feeds, external links, or integrations from third
              parties. We do not control and are not responsible for third-party content, availability, accuracy,
              or policies. Your use of third-party services is at your own risk and subject to their terms.
            </p>
          </Section>

          <Section title="9. Intellectual Property">
            <p>
              DocxsAi and its branding, software, design, and platform content are owned by us or our licensors
              and are protected by applicable intellectual property laws. Except for the limited rights expressly
              granted in these Terms, no rights are transferred to you.
            </p>
            <p>
              AI-generated outputs may not be unique. Similar or identical outputs may be produced for other users
              with similar inputs. You receive a license to use outputs you lawfully obtain through your account for
              your own business or personal purposes, subject to these Terms and applicable law.
            </p>
          </Section>

          <Section title="10. Service Availability and Changes">
            <p>
              We strive to keep DocxsAi reliable, but the Service is provided on an &quot;as available&quot; basis. Maintenance,
              updates, third-party outages, or events outside our control may cause interruptions. We may modify,
              suspend, or discontinue any part of the Service at any time.
            </p>
          </Section>

          <Section title="11. Disclaimer of Warranties">
            <p>
              To the fullest extent permitted by law, the Service and all outputs are provided &quot;as is&quot; and
              &quot;as available&quot; without warranties of any kind, whether express or implied, including merchantability,
              fitness for a particular purpose, accuracy, non-infringement, or uninterrupted operation.
            </p>
          </Section>

          <Section title="12. Limitation of Liability">
            <p>
              To the fullest extent permitted by law, DocxsAi and its operators will not be liable for any indirect,
              incidental, special, consequential, exemplary, or punitive damages, or for loss of profits, data,
              business, opportunities, or goodwill, arising from or related to your use of the Service.
            </p>
            <p>
              Our total liability for any claim arising out of or relating to the Service will not exceed the
              greater of (a) the amount you paid to DocxsAi in the 12 months before the event giving rise to the
              claim, or (b) USD $100, except where liability cannot be limited under applicable law.
            </p>
          </Section>

          <Section title="13. Indemnification">
            <p>
              You agree to indemnify and hold harmless DocxsAi and its operators from claims, losses, liabilities,
              and expenses arising out of your content, your use of the Service, your violation of these Terms, or
              your violation of any law or third-party rights.
            </p>
          </Section>

          <Section title="14. Termination">
            <p>
              You may stop using the Service at any time and may request account deletion through your account or by
              contacting support. We may suspend or terminate your access if you breach these Terms, create risk for
              the platform, or where required by law.
            </p>
            <p>
              Sections that by nature should survive termination — including disclaimers, limitations of liability,
              indemnity, and governing law — will continue to apply.
            </p>
          </Section>

          <Section title="15. Governing Law">
            <p>
              These Terms are governed by the laws of the Federal Republic of Nigeria, without regard to conflict
              of law principles. Disputes will be handled in accordance with applicable Nigerian law and courts,
              unless mandatory consumer protections in your jurisdiction require otherwise.
            </p>
          </Section>

          <Section title="16. Changes to These Terms">
            <p>
              We may update these Terms from time to time. Material changes will be posted on this page with an
              updated effective date. Continued use of the Service after changes take effect constitutes acceptance
              of the revised Terms.
            </p>
          </Section>

          <Section title="17. Contact">
            <p>
              Questions about these Terms can be sent to{' '}
              <a href={`mailto:${CONTACT_EMAIL}`} className="text-[var(--color-primary)] hover:text-[var(--color-primary-hover)] underline">
                {CONTACT_EMAIL}
              </a>{' '}
              or{' '}
              <a href={`mailto:${SUPPORT_EMAIL}`} className="text-[var(--color-primary)] hover:text-[var(--color-primary-hover)] underline">
                {SUPPORT_EMAIL}
              </a>.
            </p>
          </Section>
        </div>
      </div>
    </main>
  </div>
);

export default TermsPage;
